/// <amd-module name="Wit/AgileAnalytics/Components/WorkItemRollupProgressBar" />
import { RollupType } from "Analytics/Boards/RollupQueries/Contracts";
import * as React from "react";
import { IObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import * as Resources from "Wit/AgileAnalytics/Resources";
import { RollupProgressBarData } from "Wit/AgileAnalytics/WorkItemRollupProvider";

export interface IWorkItemRollupLabel {
    className?: string;
    icon?: JSX.Element;
    rollupData?: IObservableValue<RollupProgressBarData | undefined> | RollupProgressBarData;
    workItemType: string;
}

export const WorkItemRollupLabel: React.FunctionComponent<IWorkItemRollupLabel> = ({ className, icon, rollupData, workItemType }) => {
    return (
        <Observer rollupData={rollupData}>
            {(barProps: { rollupData: RollupProgressBarData | undefined }) => {
                const completedCount = barProps.rollupData?.completedWorkCount || 0;
                const totalCount = barProps.rollupData?.totalWorkCount || 0;
                return totalCount > 0 ? (
                    <div className={css(className, "flex-row")}>
                        {icon}
                        <Tooltip overflowOnly>
                            <span className="secondary-text text-ellipsis">{`${workItemType} ${completedCount}/${totalCount}`}</span>
                        </Tooltip>
                    </div>
                ) : null;
            }}
        </Observer>
    );
};

export interface IWorkItemRollupProgressBarProps {
    className?: string;
    icon?: JSX.Element;
    progressBarClassName?: string;
    rollupData?: IObservableValue<RollupProgressBarData | undefined> | RollupProgressBarData;
}

export const WorkItemRollupProgressBar: React.FunctionComponent<IWorkItemRollupProgressBarProps> = ({
    className,
    icon,
    progressBarClassName,
    rollupData
}) => {
    return (
        <Observer rollupData={rollupData}>
            {(barProps: { rollupData: RollupProgressBarData | undefined }) => {
                const completedCount = barProps.rollupData?.completedWorkCount;
                const totalCount = barProps.rollupData?.totalWorkCount;
                const rollupMetric = barProps.rollupData?.rollupMetric;
                const errorMessage = barProps.rollupData?.errorMessage;
                if (completedCount !== undefined && totalCount !== undefined) {
                    if (errorMessage) {
                        return (
                            <Icon ariaLabel={Resources.InfoIcon} className={"icon-margin"} iconName="Info" tooltipProps={{ text: errorMessage }} />
                        );
                    } else if (rollupMetric === RollupType.Total) {
                        return <span>{totalCount}</span>;
                    } else if (totalCount > 0) {
                        const progress = completedCount / totalCount;
                        return (
                            <div className="flex-row flex-center work-item-rollup-progress-bar">
                                {icon}
                                <Tooltip showOnFocus text={format(Resources.RollupBarTooltip, completedCount, totalCount)}>
                                    <span
                                        className={css(
                                            className,
                                            "work-item-rollup-progress-bar-container flex-row flex-grow scroll-hidden",
                                            !icon && "margin-top-1"
                                        )}
                                    >
                                        <span
                                            className={css(
                                                progressBarClassName,
                                                "work-item-rollup-progress-bar-bar",
                                                completedCount === totalCount && "completed"
                                            )}
                                            style={{
                                                transform: `scaleX(${progress})`
                                            }}
                                        />
                                    </span>
                                </Tooltip>
                                <span className={css("completion-percentage flex-self-start font-size-s secondary-text", icon && "margin-top-1")}>
                                    {`${Math.round(progress * 100)}%`}
                                </span>
                            </div>
                        );
                    } else {
                        return null;
                    }
                } else {
                    return <div className="shimmer shimmer-line work-item-rollup-progress-bar-shimmer" />;
                }
            }}
        </Observer>
    );
};
